import React, { useState } from "react";
import {
  TextField,
  Text,
  TextFieldVariant,
  ButtonVariant,
  Button,
  CopyTextField,
} from "@merge-api/merge-javascript-shared";
import { snakeCaseToSpacedCapitalized } from "../../../../../../services";
import FileUpload from "./FileUpload";
import { ConfigFieldMap } from "./hooks/usePartnershipModal";
import { getSanitizedHTML } from "../../../../../../models/Helpers";
import { OrganizationConfigStatusField } from "../../../../../../models/Entities";
import { DEFAULT_OAUTH_REDIRECT_URI, OAUTH_REDIRECT_URI_FIELD_NAME } from "./constants";

type PartnershipConfigurationFieldInputProps = {
  configField: OrganizationConfigStatusField;
  configFieldMap: ConfigFieldMap;
  handleFieldInput: (fieldInputName: string, value: string | Blob, editting?: boolean) => void;
};

// Shared label + sanitized description block used by each field variant below.
const FieldLabel = ({
  title,
  description,
  className,
}: {
  title: string;
  description?: string;
  className?: string;
}) => (
  <div className={className}>
    <Text variant="h6">{title}</Text>
    {description && (
      <div className="text-sm text-gray-40">
        <span dangerouslySetInnerHTML={{ __html: getSanitizedHTML(description) }} />
      </div>
    )}
  </div>
);

const PartnershipConfigurationFieldInput = ({
  configField,
  configFieldMap,
  handleFieldInput,
}: PartnershipConfigurationFieldInputProps) => {
  const fieldNameDisplay = snakeCaseToSpacedCapitalized(configField.field_name);
  const [isEditing, setIsEditing] = useState<boolean>(
    configFieldMap[configField.field_name].isModified,
  );
  const isCompleted = configField.completed;

  // The redirect URI is always the same value, so render it as a read-only, copyable field
  // instead of an editable input the customer has to fill in.
  if (
    configField.field_name === OAUTH_REDIRECT_URI_FIELD_NAME &&
    configField.field_type == "string"
  ) {
    return (
      <div>
        <FieldLabel
          title={fieldNameDisplay}
          description={configField.description}
          className="mb-2 px-1"
        />
        <div className="px-1">
          <CopyTextField text={DEFAULT_OAUTH_REDIRECT_URI} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {configField.field_type == "string" &&
        (isCompleted && !isEditing ? (
          <>
            <FieldLabel
              title={fieldNameDisplay}
              description={configField.description}
              className="mb-2 px-1"
            />
            <div className="flex items-center px-1">
              <div className="flex-grow-1">************</div>
              <Button
                variant={ButtonVariant.TextBlue}
                onClick={() => {
                  setIsEditing(true);
                  handleFieldInput(configField.field_name, "", true);
                }}
              >
                Change
              </Button>
            </div>
          </>
        ) : (
          <TextField
            onChange={(e) => {
              const needToSaveChanges = configField.completed == false;
              handleFieldInput(configField.field_name, e.target.value, needToSaveChanges);
            }}
            className="px-1"
            variant={TextFieldVariant.Shadowed}
            labelTitle={
              <FieldLabel
                title={fieldNameDisplay}
                description={configField.description}
                className="mb-2"
              />
            }
            value={configFieldMap[configField.field_name].value!}
            placeholder={`Enter ${fieldNameDisplay}`}
          />
        ))}
      {configField.field_type == "file" && (
        <FileUpload
          isComplete={configField.completed}
          fileHelperText={configField.description}
          fileToUpload={configField.field_name}
          filesToAccept={
            configField?.field_name.toLowerCase() == "SSL certificate file".toLowerCase()
              ? ".pem"
              : ".key,.txt"
          } // TODO: refactor so the files to accept just comes from the backend
          handleFileUpload={(fileUploaded: Blob, isEditing = false) => {
            handleFieldInput(configField.field_name, fileUploaded, isEditing);
          }}
        />
      )}
    </div>
  );
};

export default PartnershipConfigurationFieldInput;
